import { frozenPlainVars } from "./themeTokens.generated";

/**
 * Gemeinsamer Theme-Katalog aller Produkte (Entscheidung 13.10.2026: Themes liegen zentral in Shared,
 * der Systemadministrator jeder App gibt frei, welche dort wählbar sind). Ein Theme ist Stilfamilie
 * und Helligkeit zusammen — hell/dunkel ist kein eigener Modus.
 *
 * Die Kennungen spiegeln `shared/contracts/theme-catalog.json` (Backend-Validierung);
 * `shared/frontend/check-theme-catalog-contract.mjs` hält beide Seiten gleich.
 *
 * Jedes Theme liefert:
 * - die Antd-Konfiguration (Tokens und Komponenten),
 * - die CSS-Variablen `--mq-theme-*` für eigene Bausteine (Werte der früheren m1member-Sätze `--ck-*`),
 * - zwei Darstellungsmerkmale für Produktstile: Feldform und Typografie.
 */

export const themeFamilies = ["noir", "kraftraum", "classic", "slate", "kontor", "violet", "teal", "green", "vital", "web", "impuls", "pflaume"] as const;
export type ThemeFamily = (typeof themeFamilies)[number];
export type ThemeBrightness = "light" | "dark";

export const themeIds = [
  "noir-light",
  "noir-dark",
  "kraftraum-light",
  "kraftraum-dark",
  "classic-light",
  "classic-dark",
  "slate-light",
  "slate-dark",
  "kontor-light",
  "kontor-dark",
  "violet-light",
  "violet-dark",
  "teal-light",
  "teal-dark",
  "green-light",
  "green-dark",
  "vital-light",
  "vital-dark",
  "web-light",
  "web-dark",
  "impuls-light",
  "impuls-dark",
  "pflaume-light",
  "pflaume-dark",
] as const;
export type ThemeId = (typeof themeIds)[number];

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && (themeIds as readonly string[]).includes(value);
}

/** Feldform: Unterstrich, gefüllt oder umrandet (Antd-`variant` am ConfigProvider). */
export type ThemeFieldVariant = "underlined" | "filled" | "outlined";
/** `display`: Titel in Anzeigeschrift mit Ornament; `plain`: Antd-Typografie ohne Ornament. */
export type ThemeTypography = "display" | "plain";
/**
 * `kit`: Gerüst und Bedienelemente in der Gestaltung des Kits (Bühnenköpfe, Anheben, Leuchten);
 * `antd`: Erscheinungsbild wie Ant Design selbst (Schrift, Knöpfe, Schalter, heller Drawer-Kopf).
 */
export type ThemeLook = "kit" | "antd";
/**
 * Zeilenaktionen in Listen und Tabellen (`ListIconAction`): `label` Symbol plus Text (Standard, bei wenig Platz
 * nur Symbol); `icon` immer nur Symbol, Beschriftung als Tooltip und zugänglicher Name.
 */
export type ThemeListActions = "label" | "icon";

/** Namen der CSS-Variablen ohne Präfix `--mq-theme-`. */
export const themeVarNames = [
  "title-font", "title-weight", "title-style", "title-transform", "title-tracking",
  "num-weight", "num-style",
  "orn-w", "orn-h", "orn-skew",
  "radius", "radius-lg", "display", "control-h",
  "mark", "mark-ink", "mark-soft", "mark-text",
  "signal", "signal-strong",
  "bg", "surface", "surface-2", "elevated",
  "text", "text-2", "text-3", "line", "fill",
  "primary", "on-primary", "primary-text",
  "card-shadow",
  "rail", "rail-glow", "rail-line", "rail-text", "rail-strong", "rail-muted", "rail-hover", "rail-active", "rail-active-text", "rail-field",
  "page-glow", "head-glow", "head-text",
  "select", "on-select", "select-soft",
] as const;
export type ThemeVarName = (typeof themeVarNames)[number];
export type ThemeVars = Record<ThemeVarName, string>;
/**
 * Gewählter Zustand (Ankreuz-, Options-, Schaltfelder, Segmente, Reiter, Auswahl in Listen): `select` Fläche,
 * `on-select` Zeichen darauf, `select-soft` getönte Auswahlfläche (Select-Option). Kit (`--mq-kit-select`) und
 * Antd-Tokens lesen denselben Wert; Standard ist die Hauptfarbe, Noir wählt in Gold.
 */
type SelectVarName = "select" | "on-select" | "select-soft";
/** Werte-Satz einer Familie ohne die abgeleiteten Auswahlwerte (`withSelect`). */
type BaseThemeVars = Omit<ThemeVars, SelectVarName>;

export type ThemeDefinition = {
  id: ThemeId;
  family: ThemeFamily;
  brightness: ThemeBrightness;
  fieldVariant: ThemeFieldVariant;
  typography: ThemeTypography;
  look: ThemeLook;
  /** Ohne Angabe `label`. */
  listActions?: ThemeListActions;
  vars: ThemeVars;
};

export function themeIdOf(family: ThemeFamily, brightness: ThemeBrightness): ThemeId | null {
  const id = `${family}-${brightness}`;
  return isThemeId(id) ? id : null;
}

/** Gegenstück derselben Familie mit anderer Helligkeit, falls vorhanden. */
export function siblingThemeId(id: ThemeId, brightness: ThemeBrightness): ThemeId | null {
  return themeIdOf(themeDefinition(id).family, brightness);
}

// ---------------------------------------------------------------------------------------------
// Schriften
// ---------------------------------------------------------------------------------------------

const DISPLAY = "\"Inter Tight\", \"Inter\", system-ui, sans-serif";
/** Textschrift von Ant Design (Systemschriften), für die Familien mit Antd-Erscheinungsbild. */
const ANTD_SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,\n'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',\n'Noto Color Emoji'";
const WEB_SANS = "\"Outfit\", \"Inter\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif";
const PFLAUME_TITLE = "\"Poppins\", \"Open Sans\", system-ui, sans-serif";
const VITAL_SANS = "\"Inter\", \"Segoe UI Variable Text\", \"Segoe UI\", system-ui, -apple-system, Roboto, \"Helvetica Neue\", Arial, sans-serif";

// ---------------------------------------------------------------------------------------------
// Signaturfamilien Noir, Kraftraum, Klassisch (Steckbrief: m1member/frontend/ui/src/app/DESIGN_SIGNATURE.md)
// ---------------------------------------------------------------------------------------------

const noirLight: BaseThemeVars = {
  "title-font": "\"Bebas Neue\", \"Inter Tight\", \"Arial Narrow\", sans-serif",
  "title-weight": "300",
  "title-style": "normal",
  "title-transform": "uppercase",
  "title-tracking": "0.04em",
  "num-weight": "300",
  "num-style": "normal",
  "orn-w": "26px",
  "orn-h": "2px",
  "orn-skew": "0deg",
  radius: "6px",
  "radius-lg": "10px",
  display: DISPLAY,
  "control-h": "38px",
  mark: "#b8934a",
  "mark-ink": "#0a0a0a",
  "mark-soft": "rgba(184, 147, 74, 0.16)",
  "mark-text": "#8a6d33",
  signal: "#d6334a",
  "signal-strong": "#b3233a",
  bg: "#fafaf8",
  surface: "#ffffff",
  "surface-2": "#f3f3f0",
  elevated: "#ffffff",
  text: "#0a0a0a",
  "text-2": "#5f5f5b",
  "text-3": "#9a9a95",
  line: "rgba(10, 10, 10, 0.11)",
  fill: "rgba(10, 10, 10, 0.04)",
  primary: "#0a0a0a",
  "on-primary": "#fafaf8",
  "primary-text": "#0a0a0a",
  "card-shadow": "0 1px 2px rgba(10, 10, 10, 0.04)",
  rail: "#fafaf8",
  "rail-glow": "radial-gradient(140% 38% at 0% 100%, rgba(184, 147, 74, 0.14), transparent 72%)",
  "rail-line": "rgba(10, 10, 10, 0.09)",
  "rail-text": "#5f5f5b",
  "rail-strong": "#0a0a0a",
  "rail-muted": "#9a9a95",
  "rail-hover": "rgba(10, 10, 10, 0.04)",
  "rail-active": "transparent",
  "rail-active-text": "#0a0a0a",
  "rail-field": "rgba(10, 10, 10, 0.03)",
  "page-glow": "radial-gradient(55% 300px at 0% 0%, rgba(184, 147, 74, 0.10), transparent 70%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(200, 169, 106, 0.38), transparent 62%), #0a0a0a",
  "head-text": "#f5f5f2",
};

const noirDark: BaseThemeVars = {
  ...noirLight,
  mark: "#c8a96a",
  "mark-soft": "rgba(200, 169, 106, 0.16)",
  "mark-text": "#d9bd85",
  signal: "#ff5a6e",
  "signal-strong": "#ff7a8a",
  bg: "#070707",
  surface: "#0f0f0f",
  "surface-2": "#151515",
  elevated: "#121212",
  text: "rgba(245, 245, 242, 0.94)",
  "text-2": "rgba(245, 245, 242, 0.62)",
  "text-3": "rgba(245, 245, 242, 0.40)",
  line: "rgba(255, 255, 255, 0.10)",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#f5f5f2",
  "on-primary": "#070707",
  "primary-text": "#f5f5f2",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#050505",
  "rail-glow": "radial-gradient(140% 38% at 0% 100%, rgba(200, 169, 106, 0.18), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(245, 245, 242, 0.60)",
  "rail-strong": "#f5f5f2",
  "rail-muted": "rgba(245, 245, 242, 0.38)",
  "rail-hover": "rgba(255, 255, 255, 0.04)",
  "rail-active-text": "#f5f5f2",
  "rail-field": "rgba(255, 255, 255, 0.04)",
  "page-glow": "radial-gradient(55% 320px at 0% 0%, rgba(200, 169, 106, 0.09), transparent 70%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(200, 169, 106, 0.30), transparent 62%), #141414",
};

const kraftraumLight: BaseThemeVars = {
  "title-font": "\"Big Shoulders Display\", \"Inter Tight\", \"Arial Narrow\", sans-serif",
  "title-weight": "900",
  "title-style": "oblique 8deg",
  "title-transform": "uppercase",
  "title-tracking": "0",
  "num-weight": "800",
  "num-style": "oblique 6deg",
  "orn-w": "9px",
  "orn-h": "0.85em",
  "orn-skew": "-18deg",
  radius: "4px",
  "radius-lg": "8px",
  display: DISPLAY,
  "control-h": "38px",
  mark: "#ffd60a",
  "mark-ink": "#111214",
  "mark-soft": "rgba(255, 214, 10, 0.24)",
  "mark-text": "#7a5a00",
  signal: "#ff3b47",
  "signal-strong": "#d1202e",
  bg: "#f2f2ee",
  surface: "#ffffff",
  "surface-2": "#f7f7f3",
  elevated: "#ffffff",
  text: "#111214",
  "text-2": "#53555a",
  "text-3": "#8a8c91",
  line: "rgba(17, 18, 20, 0.10)",
  fill: "rgba(17, 18, 20, 0.05)",
  primary: "#111214",
  "on-primary": "#ffffff",
  "primary-text": "#111214",
  "card-shadow": "0 1px 0 rgba(17, 18, 20, 0.04)",
  rail: "#111214",
  "rail-glow": "radial-gradient(150% 42% at 0% 100%, rgba(255, 214, 10, 0.16), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(255, 255, 255, 0.72)",
  "rail-strong": "#ffffff",
  "rail-muted": "rgba(255, 255, 255, 0.42)",
  "rail-hover": "rgba(255, 255, 255, 0.06)",
  "rail-active": "rgba(255, 255, 255, 0.09)",
  "rail-active-text": "#ffd60a",
  "rail-field": "rgba(255, 255, 255, 0.06)",
  "page-glow": "radial-gradient(50% 260px at 0% 0%, rgba(255, 214, 10, 0.12), transparent 70%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(255, 214, 10, 0.30), transparent 60%), #111214",
  "head-text": "#ffffff",
};

const kraftraumDark: BaseThemeVars = {
  ...kraftraumLight,
  bg: "#0b0c0d",
  surface: "#151618",
  "surface-2": "#1b1c1f",
  elevated: "#1e2023",
  text: "rgba(255, 255, 255, 0.93)",
  "text-2": "rgba(255, 255, 255, 0.64)",
  "text-3": "rgba(255, 255, 255, 0.42)",
  line: "rgba(255, 255, 255, 0.08)",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#ffd60a",
  "on-primary": "#111214",
  "primary-text": "#ffd60a",
  "mark-soft": "rgba(255, 214, 10, 0.16)",
  "mark-text": "#ffd60a",
  "signal-strong": "#ff6b74",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.05)",
  rail: "#0e0f10",
  "page-glow": "radial-gradient(50% 280px at 0% 0%, rgba(255, 214, 10, 0.07), transparent 70%)",
};

const classicLight: BaseThemeVars = {
  "title-font": ANTD_SANS,
  "title-weight": "600",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "0",
  "num-weight": "600",
  "num-style": "normal",
  "orn-w": "0px",
  "orn-h": "0px",
  "orn-skew": "0deg",
  radius: "6px",
  "radius-lg": "8px",
  display: ANTD_SANS,
  "control-h": "32px",
  mark: "#1677ff",
  "mark-ink": "#ffffff",
  "mark-soft": "rgba(22, 119, 255, 0.12)",
  "mark-text": "#1677ff",
  signal: "#ff4d4f",
  "signal-strong": "#d9363e",
  bg: "#f5f5f5",
  surface: "#ffffff",
  "surface-2": "#fafafa",
  elevated: "#ffffff",
  text: "rgba(0, 0, 0, 0.88)",
  "text-2": "rgba(0, 0, 0, 0.65)",
  "text-3": "rgba(0, 0, 0, 0.45)",
  line: "rgba(0, 0, 0, 0.10)",
  fill: "rgba(0, 0, 0, 0.04)",
  primary: "#1677ff",
  "on-primary": "#ffffff",
  "primary-text": "#1677ff",
  "card-shadow": "0 1px 2px rgba(0, 0, 0, 0.03)",
  rail: "#ffffff",
  "rail-glow": "none",
  "rail-line": "rgba(0, 0, 0, 0.06)",
  "rail-text": "rgba(0, 0, 0, 0.65)",
  "rail-strong": "rgba(0, 0, 0, 0.88)",
  "rail-muted": "rgba(0, 0, 0, 0.45)",
  "rail-hover": "rgba(0, 0, 0, 0.04)",
  "rail-active": "#e6f4ff",
  "rail-active-text": "#1677ff",
  "rail-field": "rgba(0, 0, 0, 0.04)",
  "page-glow": "none",
  "head-glow": "#1f1f1f",
  "head-text": "#ffffff",
};

const classicDark: BaseThemeVars = {
  ...classicLight,
  signal: "#dc4446",
  "signal-strong": "#e86e6b",
  bg: "#000000",
  surface: "#141414",
  "surface-2": "#1f1f1f",
  elevated: "#1f1f1f",
  text: "rgba(255, 255, 255, 0.85)",
  "text-2": "rgba(255, 255, 255, 0.65)",
  "text-3": "rgba(255, 255, 255, 0.45)",
  line: "rgba(255, 255, 255, 0.12)",
  fill: "rgba(255, 255, 255, 0.08)",
  "primary-text": "#4096ff",
  "mark-soft": "rgba(22, 119, 255, 0.18)",
  "mark-text": "#4096ff",
  "card-shadow": "none",
  rail: "#141414",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(255, 255, 255, 0.65)",
  "rail-strong": "rgba(255, 255, 255, 0.85)",
  "rail-muted": "rgba(255, 255, 255, 0.45)",
  "rail-hover": "rgba(255, 255, 255, 0.08)",
  "rail-active": "#111a2c",
  "rail-active-text": "#4096ff",
  "rail-field": "rgba(255, 255, 255, 0.08)",
};


// ---------------------------------------------------------------------------------------------
// Familien Schiefer und Kontor (Stufen 3–4 des Plans 2026-10-13; Steckbriefe: theme/STECKBRIEFE.md).
// Vorgeschlagen für mQguard (Schiefer) und m2company (Kontor); wie alle Themes in
// jeder App wählbar, sofern der Systemadministrator sie freigibt.
// ---------------------------------------------------------------------------------------------

const slateLight: BaseThemeVars = {
  "title-font": DISPLAY,
  "title-weight": "700",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.01em",
  "num-weight": "600",
  "num-style": "normal",
  "orn-w": "3px",
  "orn-h": "0.9em",
  "orn-skew": "0deg",
  radius: "6px",
  "radius-lg": "10px",
  display: DISPLAY,
  "control-h": "36px",
  mark: "#06b6d4",
  "mark-ink": "#0b1220",
  "mark-soft": "rgba(6, 182, 212, 0.13)",
  "mark-text": "#0e7490",
  signal: "#e11d48",
  "signal-strong": "#be123c",
  bg: "#f3f5f8",
  surface: "#ffffff",
  "surface-2": "#f8fafc",
  elevated: "#ffffff",
  text: "#0f172a",
  "text-2": "#475569",
  "text-3": "#94a3b8",
  line: "rgba(15, 23, 42, 0.10)",
  fill: "rgba(15, 23, 42, 0.04)",
  primary: "#0e7490",
  "on-primary": "#ffffff",
  "primary-text": "#0e7490",
  "card-shadow": "0 1px 2px rgba(15, 23, 42, 0.05)",
  rail: "#0f172a",
  "rail-glow": "radial-gradient(140% 40% at 0% 100%, rgba(34, 211, 238, 0.16), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(226, 232, 240, 0.72)",
  "rail-strong": "#f8fafc",
  "rail-muted": "rgba(226, 232, 240, 0.42)",
  "rail-hover": "rgba(255, 255, 255, 0.06)",
  "rail-active": "rgba(34, 211, 238, 0.12)",
  "rail-active-text": "#67e8f9",
  "rail-field": "rgba(255, 255, 255, 0.06)",
  "page-glow": "radial-gradient(50% 260px at 100% 0%, rgba(6, 182, 212, 0.07), transparent 70%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(34, 211, 238, 0.28), transparent 62%), #0f172a",
  "head-text": "#f8fafc",
};

const slateDark: BaseThemeVars = {
  ...slateLight,
  mark: "#22d3ee",
  "mark-soft": "rgba(34, 211, 238, 0.14)",
  "mark-text": "#67e8f9",
  signal: "#fb7185",
  "signal-strong": "#fda4af",
  bg: "#0b1120",
  surface: "#111827",
  "surface-2": "#152033",
  elevated: "#182336",
  text: "rgba(241, 245, 249, 0.94)",
  "text-2": "rgba(203, 213, 225, 0.70)",
  "text-3": "rgba(148, 163, 184, 0.62)",
  line: "rgba(255, 255, 255, 0.09)",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#22d3ee",
  "on-primary": "#0b1120",
  "primary-text": "#67e8f9",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#070c18",
  "page-glow": "radial-gradient(50% 280px at 100% 0%, rgba(34, 211, 238, 0.06), transparent 70%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(34, 211, 238, 0.24), transparent 62%), #111827",
};

const kontorLight: BaseThemeVars = {
  "title-font": DISPLAY,
  "title-weight": "650",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.005em",
  "num-weight": "600",
  "num-style": "normal",
  "orn-w": "14px",
  "orn-h": "3px",
  "orn-skew": "0deg",
  radius: "4px",
  "radius-lg": "8px",
  display: DISPLAY,
  "control-h": "36px",
  mark: "#c8963e",
  "mark-ink": "#1b2230",
  "mark-soft": "rgba(200, 150, 62, 0.16)",
  "mark-text": "#8a6424",
  signal: "#c2410c",
  "signal-strong": "#9a3412",
  bg: "#f5f3ee",
  surface: "#fffefb",
  "surface-2": "#f9f7f2",
  elevated: "#ffffff",
  text: "#1b2230",
  "text-2": "#535b69",
  "text-3": "#8b919c",
  line: "rgba(27, 34, 48, 0.11)",
  fill: "rgba(27, 34, 48, 0.045)",
  primary: "#1f5f4a",
  "on-primary": "#ffffff",
  "primary-text": "#1f5f4a",
  "card-shadow": "0 1px 0 rgba(27, 34, 48, 0.05)",
  rail: "#1b2230",
  "rail-glow": "radial-gradient(140% 38% at 0% 100%, rgba(200, 150, 62, 0.14), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(255, 255, 255, 0.70)",
  "rail-strong": "#ffffff",
  "rail-muted": "rgba(255, 255, 255, 0.42)",
  "rail-hover": "rgba(255, 255, 255, 0.06)",
  "rail-active": "rgba(255, 255, 255, 0.09)",
  "rail-active-text": "#e2bd73",
  "rail-field": "rgba(255, 255, 255, 0.06)",
  "page-glow": "none",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(200, 150, 62, 0.26), transparent 62%), #1b2230",
  "head-text": "#ffffff",
};

const kontorDark: BaseThemeVars = {
  ...kontorLight,
  mark: "#d9ab57",
  "mark-soft": "rgba(217, 171, 87, 0.16)",
  "mark-text": "#e2bd73",
  signal: "#f97316",
  "signal-strong": "#fb923c",
  bg: "#101318",
  surface: "#171b22",
  "surface-2": "#1c212a",
  elevated: "#1f242d",
  text: "rgba(245, 242, 235, 0.92)",
  "text-2": "rgba(245, 242, 235, 0.64)",
  "text-3": "rgba(245, 242, 235, 0.42)",
  line: "rgba(255, 255, 255, 0.08)",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#4fae8a",
  "on-primary": "#0f1a15",
  "primary-text": "#6cc3a0",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#0b0e12",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(217, 171, 87, 0.22), transparent 62%), #1c212a",
};

type DesignedFamily = "slate" | "kontor";


function designed(family: DesignedFamily, brightness: ThemeBrightness, base: BaseThemeVars, fieldVariant: ThemeFieldVariant): ThemeDefinition {
  const vars = withSelect(family, brightness, base);
  return { id: `${family}-${brightness}` as ThemeId, family, brightness, fieldVariant, typography: "display", look: "kit", vars };
}

// ---------------------------------------------------------------------------------------------
// Antd-Familien Violett, Türkis, Grün: Antd-Grundwerte mit Markenfarbe (bisheriges Erscheinungsbild
// von mQmarketing, mQguard und m2company; Hauptfarben #722ed1, #13c2c2, #52c41a, Rundung 8). Die CSS-Werte hat
// bis Stufe 8 Antd aus seinen Tokens berechnet; sie sind unverändert in `themeTokens.generated.ts` eingefroren.
// ---------------------------------------------------------------------------------------------



// ---------------------------------------------------------------------------------------------
// Vital: Petrol und Koralle (Werte aus my-vitalteam `backend/wwwroot/mvt-tokens.css`).
// ---------------------------------------------------------------------------------------------

const vitalLight: BaseThemeVars = {
  "title-font": `"Manrope", ${VITAL_SANS}`,
  "title-weight": "700",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.01em",
  "num-weight": "700",
  "num-style": "normal",
  "orn-w": "0px",
  "orn-h": "0px",
  "orn-skew": "0deg",
  radius: "12px",
  "radius-lg": "18px",
  display: `"Manrope", ${VITAL_SANS}`,
  "control-h": "42px",
  mark: "#ff7a59",
  "mark-ink": "#ffffff",
  "mark-soft": "rgba(13, 148, 136, 0.12)",
  "mark-text": "#0d9488",
  signal: "#d64545",
  "signal-strong": "#b83333",
  bg: "#eef6f3",
  surface: "#ffffff",
  "surface-2": "#f5faf8",
  elevated: "#ffffff",
  text: "#0a3b37",
  "text-2": "#557069",
  "text-3": "#8aa39c",
  line: "#dfece7",
  fill: "#edf7f4",
  primary: "#0d9488",
  "on-primary": "#ffffff",
  "primary-text": "#0d9488",
  // Ruhender Kartenschatten wie my-vitalteam (`--mvt-shadow-sm`, gemessen 14.10.2026).
  "card-shadow": "0 1px 2px rgba(10, 59, 55, 0.05), 0 6px 18px -10px rgba(10, 59, 55, 0.16)",
  // Navigation, Seitenfläche und Drawer-Kopf wie der my-vitalteam-Rahmen (`--mvt-on-dark-*`,
  // `--mvt-grad-sidebar-glow`, `--mvt-grad-app`, `--mvt-grad-tile-teal`; gemessen 14.10.2026).
  rail: "#0a3b37",
  "rail-glow": "radial-gradient(120% 55% at 0% 100%, rgba(255, 122, 89, 0.22), transparent 60%)",
  "rail-line": "rgba(255, 255, 255, 0.12)",
  "rail-text": "rgba(255, 255, 255, 0.78)",
  "rail-strong": "#ffffff",
  "rail-muted": "rgba(255, 255, 255, 0.52)",
  "rail-hover": "rgba(255, 255, 255, 0.08)",
  "rail-active": "rgba(255, 255, 255, 0.14)",
  "rail-active-text": "#ffffff",
  "rail-field": "rgba(255, 255, 255, 0.06)",
  "page-glow": "radial-gradient(1200px 520px at 100% -10%, rgba(13, 148, 136, 0.10), transparent 60%), radial-gradient(900px 420px at -10% 110%, rgba(255, 122, 89, 0.07), transparent 60%)",
  "head-glow": "linear-gradient(150deg, #e9fbf5, #cdf2e6)",
  "head-text": "#0a3b37",
};

const vitalDark: BaseThemeVars = {
  ...vitalLight,
  mark: "#ff8a6b",
  "mark-ink": "#2a0e06",
  "mark-soft": "rgba(45, 212, 191, 0.14)",
  "mark-text": "#5eead4",
  signal: "#f87171",
  "signal-strong": "#fca5a5",
  bg: "#071f1d",
  surface: "#0c2a27",
  "surface-2": "#10332f",
  elevated: "#123a35",
  text: "rgba(230, 247, 242, 0.94)",
  "text-2": "rgba(230, 247, 242, 0.68)",
  "text-3": "rgba(230, 247, 242, 0.45)",
  line: "rgba(255, 255, 255, 0.09)",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#2dd4bf",
  "on-primary": "#042f2b",
  "primary-text": "#5eead4",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.05)",
  rail: "#041816",
  "page-glow": "none",
  "head-glow": "linear-gradient(135deg, #0f766e 0%, #042f2b 100%)",
  "head-text": "#ffffff",
};


// ---------------------------------------------------------------------------------------------
// mQorva Web: Erscheinungsbild der Website mqorva.de (Werte aus `website/src/styles/global.css`).
// Dunkel = „Ink“ (Graphit, Signal-Gelb), hell = „Paper“ (warmes Off-White; Gelb nur als Fläche, nie als Schrift).
// Gedacht für Seiten, die in die Website eingebettet werden (Tarifauswahl), wie alle Themes aber in jeder App wählbar.
// ---------------------------------------------------------------------------------------------

const webDark: BaseThemeVars = {
  "title-font": WEB_SANS,
  "title-weight": "600",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.01em",
  "num-weight": "600",
  "num-style": "normal",
  "orn-w": "0px",
  "orn-h": "0px",
  "orn-skew": "0deg",
  radius: "10px",
  "radius-lg": "14px",
  display: WEB_SANS,
  "control-h": "40px",
  mark: "#ffd60a",
  "mark-ink": "#1f1a00",
  "mark-soft": "rgba(255, 214, 10, 0.14)",
  "mark-text": "#ffd60a",
  signal: "#ff5a5f",
  "signal-strong": "#ff7a7e",
  bg: "#0e0f11",
  surface: "#17181b",
  "surface-2": "#1d1e22",
  elevated: "#1d1e22",
  text: "#f3f1ec",
  "text-2": "#a3a19b",
  "text-3": "rgba(243, 241, 236, 0.42)",
  line: "#27292d",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#ffd60a",
  "on-primary": "#1f1a00",
  "primary-text": "#ffd60a",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#0b0c0e",
  "rail-glow": "radial-gradient(140% 40% at 0% 100%, rgba(255, 214, 10, 0.12), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(243, 241, 236, 0.66)",
  "rail-strong": "#f3f1ec",
  "rail-muted": "rgba(243, 241, 236, 0.42)",
  "rail-hover": "rgba(255, 255, 255, 0.05)",
  "rail-active": "rgba(255, 214, 10, 0.12)",
  "rail-active-text": "#ffd60a",
  "rail-field": "rgba(255, 255, 255, 0.05)",
  "page-glow": "radial-gradient(110% 85% at 88% 0%, rgba(255, 214, 10, 0.07), transparent 60%)",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(255, 214, 10, 0.20), transparent 62%), #17181b",
  "head-text": "#f3f1ec",
};

const webLight: BaseThemeVars = {
  ...webDark,
  "mark-soft": "rgba(255, 214, 10, 0.22)",
  "mark-text": "#121212",
  signal: "#d92d3a",
  "signal-strong": "#b3202c",
  bg: "#f3f1ec",
  surface: "#ffffff",
  "surface-2": "#faf9f6",
  elevated: "#ffffff",
  text: "#121212",
  "text-2": "#4f4d48",
  "text-3": "#8a877f",
  line: "#e2ded5",
  fill: "rgba(18, 18, 18, 0.04)",
  "primary-text": "#121212",
  "card-shadow": "0 1px 2px rgba(18, 18, 18, 0.04), 0 10px 30px rgba(18, 18, 18, 0.06)",
  rail: "#0e0f11",
  "page-glow": "none",
  "head-glow": "radial-gradient(90% 140% at 100% 0%, rgba(255, 214, 10, 0.22), transparent 62%), #0e0f11",
};


// ---------------------------------------------------------------------------------------------
// Impuls: Erscheinungsbild für Marketing (vorgeschlagen für mQmarketing, neu gefasst 16.10.2026). Warmes Graphit als
// Gerüst, Koralle-Orange als Aktionsfarbe (Aufmerksamkeit, Wachstum), dunkleres Koralle als Akzent (`mark`: Hover,
// Links, Kennzahl-Symbole); kein Violett (Ronny, 16.10.2026: passt nicht). Ruhige Flächen, damit Bilder und Anzeigen der Kunden wirken; keine Verlaufsflächen auf Bedienelementen.
// Outfit als Anzeigeschrift, weiche Rundungen. In hell und dunkel auf WCAG AA geprüft (Text auf Fläche, Leiste, Knopf).
// ---------------------------------------------------------------------------------------------

const impulsLight: BaseThemeVars = {
  "title-font": WEB_SANS,
  "title-weight": "700",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.015em",
  "num-weight": "700",
  "num-style": "normal",
  "orn-w": "0px",
  "orn-h": "0px",
  "orn-skew": "0deg",
  radius: "12px",
  "radius-lg": "18px",
  display: WEB_SANS,
  "control-h": "40px",
  mark: "#a8360f",
  "mark-ink": "#ffffff",
  "mark-soft": "rgba(201, 67, 27, 0.10)",
  "mark-text": "#a8360f",
  signal: "#dc2626",
  "signal-strong": "#b91c1c",
  bg: "#f7f5f2",
  surface: "#ffffff",
  "surface-2": "#faf8f5",
  elevated: "#ffffff",
  text: "#1c1917",
  "text-2": "#57534e",
  "text-3": "#78716c",
  line: "#e9e4dd",
  fill: "rgba(28, 25, 23, 0.04)",
  primary: "#c9431b",
  "on-primary": "#ffffff",
  "primary-text": "#b23a16",
  "card-shadow": "0 1px 2px rgba(28, 25, 23, 0.05), 0 8px 24px rgba(28, 25, 23, 0.05)",
  rail: "#1f1b18",
  "rail-glow": "radial-gradient(120% 40% at 100% 0%, rgba(201, 67, 27, 0.16), transparent 70%)",
  "rail-line": "rgba(255, 255, 255, 0.08)",
  "rail-text": "rgba(245, 241, 236, 0.74)",
  "rail-strong": "#ffffff",
  "rail-muted": "rgba(245, 241, 236, 0.48)",
  "rail-hover": "rgba(255, 255, 255, 0.06)",
  "rail-active": "rgba(255, 255, 255, 0.10)",
  "rail-active-text": "#ffffff",
  "rail-field": "rgba(255, 255, 255, 0.07)",
  "page-glow": "none",
  "head-glow": "radial-gradient(120% 160% at 100% 0%, rgba(201, 67, 27, 0.42), transparent 60%), #1f1b18",
  "head-text": "#ffffff",
};

const impulsDark: BaseThemeVars = {
  ...impulsLight,
  mark: "#ff8a5c",
  "mark-ink": "#1a1410",
  "mark-soft": "rgba(242, 107, 58, 0.14)",
  "mark-text": "#ff9a6b",
  signal: "#f87171",
  "signal-strong": "#fca5a5",
  bg: "#12100e",
  surface: "#1b1815",
  "surface-2": "#211d1a",
  elevated: "#26221e",
  text: "#f5f1ec",
  "text-2": "#b9b1a8",
  "text-3": "rgba(245, 241, 236, 0.48)",
  line: "#2f2a25",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#f26b3a",
  "on-primary": "#1a1410",
  "primary-text": "#ff9a6b",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#0e0c0a",
  "rail-glow": "radial-gradient(120% 40% at 100% 0%, rgba(242, 107, 58, 0.12), transparent 70%)",
  "head-glow": "radial-gradient(120% 160% at 100% 0%, rgba(242, 107, 58, 0.30), transparent 60%), #1b1815",
};


// ---------------------------------------------------------------------------------------------
// Pflaume (31.10.2026): Studio-Erscheinungsbild aus Pflaume, Markenviolett und Limette (Werte der Website
// grazioes-sport.de, `src/styles/global.css`). Pflaume trägt Leiste und Kopf, Markenviolett ist die Aktionsfarbe,
// Limette der Akzent (`mark`: Flächen, nie als Schrift auf Hell). Poppins für Titel, Open Sans im Text,
// weiche Rundungen wie die Website. Vorgeschlagen für den Website-Admin, wie alle Themes in jeder App wählbar.
// ---------------------------------------------------------------------------------------------

const pflaumeLight: BaseThemeVars = {
  "title-font": PFLAUME_TITLE,
  "title-weight": "700",
  "title-style": "normal",
  "title-transform": "none",
  "title-tracking": "-0.02em",
  "num-weight": "700",
  "num-style": "normal",
  "orn-w": "0px",
  "orn-h": "0px",
  "orn-skew": "0deg",
  radius: "12px",
  "radius-lg": "18px",
  display: PFLAUME_TITLE,
  "control-h": "40px",
  mark: "#a5dc1a",
  "mark-ink": "#2d0729",
  "mark-soft": "rgba(165, 220, 26, 0.20)",
  "mark-text": "#4a6a00",
  signal: "#c0262d",
  "signal-strong": "#9b1c22",
  bg: "#f7f3f6",
  surface: "#ffffff",
  "surface-2": "#fbf4fa",
  elevated: "#ffffff",
  text: "#23121f",
  "text-2": "#6a5866",
  "text-3": "#8a7686",
  line: "#ecdde9",
  fill: "rgba(45, 7, 41, 0.04)",
  primary: "#7a1d75",
  "on-primary": "#ffffff",
  "primary-text": "#7a1d75",
  "card-shadow": "0 1px 2px rgba(45, 7, 41, 0.06), 0 2px 8px rgba(45, 7, 41, 0.05)",
  rail: "#2d0729",
  "rail-glow": "radial-gradient(120% 40% at 0% 100%, rgba(165, 220, 26, 0.14), transparent 72%)",
  "rail-line": "rgba(255, 255, 255, 0.12)",
  "rail-text": "rgba(255, 255, 255, 0.82)",
  "rail-strong": "#ffffff",
  "rail-muted": "rgba(255, 255, 255, 0.60)",
  "rail-hover": "rgba(255, 255, 255, 0.08)",
  "rail-active": "rgba(255, 255, 255, 0.14)",
  "rail-active-text": "#ffffff",
  "rail-field": "rgba(255, 255, 255, 0.08)",
  "page-glow": "none",
  "head-glow": "radial-gradient(120% 160% at 100% 0%, rgba(165, 220, 26, 0.24), transparent 60%), #2d0729",
  "head-text": "#ffffff",
};

const pflaumeDark: BaseThemeVars = {
  ...pflaumeLight,
  "mark-ink": "#1f041d",
  "mark-soft": "rgba(165, 220, 26, 0.14)",
  "mark-text": "#b9ea3c",
  signal: "#f87171",
  "signal-strong": "#fca5a5",
  bg: "#160313",
  surface: "#1f061c",
  "surface-2": "#270a23",
  elevated: "#2c0b28",
  text: "#f7eef5",
  "text-2": "#c9b5c4",
  "text-3": "rgba(247, 238, 245, 0.50)",
  line: "#3a1535",
  fill: "rgba(255, 255, 255, 0.05)",
  primary: "#c063b8",
  "on-primary": "#1f041d",
  "primary-text": "#e3a6dc",
  "card-shadow": "inset 0 1px 0 rgba(255, 255, 255, 0.04)",
  rail: "#12020f",
  "rail-glow": "radial-gradient(120% 40% at 0% 100%, rgba(165, 220, 26, 0.10), transparent 72%)",
  "head-glow": "radial-gradient(120% 160% at 100% 0%, rgba(165, 220, 26, 0.16), transparent 60%), #1f061c",
};


// ---------------------------------------------------------------------------------------------
// Auswahlfarbe und Antd-Grundtokens der Kit-Familien
// ---------------------------------------------------------------------------------------------

const SHORT_HEX_LENGTH = 3;
const HEX_CHANNEL_OFFSETS = [0, 2, 4];
const HEX_CHANNEL_LENGTH = 2;
const HEX_RADIX = 16;
const SELECT_SOFT_ALPHA_DARK = 0.2;
const SELECT_SOFT_ALPHA_LIGHT = 0.12;

/** Farbe mit Deckkraft; versteht `#rgb`, `#rrggbb` und `rgb()/rgba()`, sonst unverändert. */
function withAlpha(color: string, alpha: number): string {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color.trim());
  if (hex) {
    const digits = hex[1].length === SHORT_HEX_LENGTH ? hex[1].split("").map((d) => d + d).join("") : hex[1];
    const [r, g, b] = HEX_CHANNEL_OFFSETS.map((i) => Number.parseInt(digits.slice(i, i + HEX_CHANNEL_LENGTH), HEX_RADIX));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(color.trim());
  return rgb ? `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})` : color;
}

/** Ergänzt die Auswahlwerte: Noir wählt in der Akzentfarbe (Gold), alle anderen in der Hauptfarbe. */
function withSelect(family: ThemeFamily, brightness: ThemeBrightness, base: BaseThemeVars): ThemeVars {
  const gold = family === "noir";
  const select = gold ? base.mark : base.primary;
  return {
    ...base,
    select,
    "on-select": gold ? base["mark-ink"] : base["on-primary"],
    "select-soft": withAlpha(select, brightness === "dark" ? SELECT_SOFT_ALPHA_DARK : SELECT_SOFT_ALPHA_LIGHT),
  };
}



// ---------------------------------------------------------------------------------------------
// Katalog
// ---------------------------------------------------------------------------------------------

function signature(family: "noir" | "kraftraum" | "classic", brightness: ThemeBrightness, base: BaseThemeVars, fieldVariant: ThemeFieldVariant): ThemeDefinition {
  const vars = withSelect(family, brightness, base);
  return {
    id: `${family}-${brightness}` as ThemeId,
    family,
    brightness,
    fieldVariant,
    typography: family === "classic" ? "plain" : "display",
    look: family === "classic" ? "antd" : "kit",
    vars,
  };
}

function plain(family: "violet" | "teal" | "green", brightness: ThemeBrightness): ThemeDefinition {
  const id = `${family}-${brightness}` as ThemeId;
  return { id, family, brightness, fieldVariant: "outlined", typography: "plain", look: "antd", vars: frozenPlainVars[id] as ThemeVars };
}

const definitions: Record<ThemeId, ThemeDefinition> = {
  "noir-light": signature("noir", "light", noirLight, "underlined"),
  "noir-dark": signature("noir", "dark", noirDark, "underlined"),
  "kraftraum-light": signature("kraftraum", "light", kraftraumLight, "filled"),
  "kraftraum-dark": signature("kraftraum", "dark", kraftraumDark, "filled"),
  "classic-light": signature("classic", "light", classicLight, "outlined"),
  "classic-dark": signature("classic", "dark", classicDark, "outlined"),
  "slate-light": designed("slate", "light", slateLight, "outlined"),
  "slate-dark": designed("slate", "dark", slateDark, "outlined"),
  "kontor-light": designed("kontor", "light", kontorLight, "outlined"),
  "kontor-dark": designed("kontor", "dark", kontorDark, "outlined"),
  "violet-light": plain("violet", "light"),
  "violet-dark": plain("violet", "dark"),
  "teal-light": plain("teal", "light"),
  "teal-dark": plain("teal", "dark"),
  "green-light": plain("green", "light"),
  "green-dark": plain("green", "dark"),
  "vital-light": { id: "vital-light", family: "vital", brightness: "light", fieldVariant: "outlined", typography: "plain", look: "kit", listActions: "icon", vars: withSelect("vital", "light", vitalLight) },
  "vital-dark": { id: "vital-dark", family: "vital", brightness: "dark", fieldVariant: "outlined", typography: "plain", look: "kit", listActions: "icon", vars: withSelect("vital", "dark", vitalDark) },
  "web-light": { id: "web-light", family: "web", brightness: "light", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("web", "light", webLight) },
  "web-dark": { id: "web-dark", family: "web", brightness: "dark", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("web", "dark", webDark) },
  "impuls-light": { id: "impuls-light", family: "impuls", brightness: "light", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("impuls", "light", impulsLight) },
  "impuls-dark": { id: "impuls-dark", family: "impuls", brightness: "dark", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("impuls", "dark", impulsDark) },
  "pflaume-light": { id: "pflaume-light", family: "pflaume", brightness: "light", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("pflaume", "light", pflaumeLight) },
  "pflaume-dark": { id: "pflaume-dark", family: "pflaume", brightness: "dark", fieldVariant: "outlined", typography: "display", look: "kit", vars: withSelect("pflaume", "dark", pflaumeDark) },
};

export function themeDefinition(id: ThemeId): ThemeDefinition {
  return definitions[id];
}

export const themeCatalog: readonly ThemeDefinition[] = themeIds.map((id) => definitions[id]);

/** Übersetzungsschlüssel der Familie (`themeTranslations.ts`). */
export function themeFamilyLabelKey(family: ThemeFamily): string {
  return `Theme.family.${family}`;
}

/** Übersetzungsschlüssel der Helligkeit (`themeTranslations.ts`). */
export function themeBrightnessLabelKey(brightness: ThemeBrightness): string {
  return `Theme.brightness.${brightness}`;
}

/** Vollständiger Anzeigename, z. B. „Noir dunkel“. */
export function themeLabel(id: ThemeId, tr: (key: string) => string): string {
  const definition = themeDefinition(id);
  return `${tr(themeFamilyLabelKey(definition.family))} ${tr(themeBrightnessLabelKey(definition.brightness))}`;
}
